import { Eye } from "lucide-react";
import {
  SAMPLE_PLACEHOLDER_CONTEXT,
  messageHasPlaceholders,
  substituteMessageVars,
  type MessageVarContext,
} from "@/lib/message-placeholders";
import { BOOKING_REQUEST_LINK_TOKEN_RE } from "@shared/booking-request-link-token";

interface MessagePlaceholderPreviewProps {
  // The raw composed message (may contain {{token}} placeholders).
  content: string;
  // Real values for a specific recipient, when one is known. Any field left
  // empty/null falls back to the sample value so no stray {{token}} ever shows.
  recipientContext?: Partial<MessageVarContext> | null;
  // Display name of the recipient whose real values are being used, if any.
  recipientName?: string | null;
  className?: string;
}

// {{booking_request_link:<id>}} tokens resolve server-side at send time (to
// the template's current share link + a per-member ?m= identity param), so the
// preview swaps them for an illustrative URL locally — deliberately NOT part
// of the client/server `substituteMessageVars` lockstep pair.
const BOOKING_LINK_TOKEN_RE = new RegExp(BOOKING_REQUEST_LINK_TOKEN_RE.source, "g");

function previewBookingLinkTokens(body: string): string {
  return body.replace(
    BOOKING_LINK_TOKEN_RE,
    `${window.location.origin}/booking-request/your-form-link?m=…`,
  );
}

// Live preview of how a composed message reads once its placeholder tokens are
// substituted. Uses the selected recipient's real values where known, otherwise
// sample values. Renders nothing unless the message actually contains a known
// placeholder token. Substitution mirrors the server send path via
// `substituteMessageVars`.
export function MessagePlaceholderPreview({
  content,
  recipientContext,
  recipientName,
  className,
}: MessagePlaceholderPreviewProps) {
  // .match (not .test) — a `g` regex's .test is stateful via lastIndex and
  // would alternate across re-renders.
  const hasBookingLinkToken = content.match(BOOKING_LINK_TOKEN_RE) != null;
  if (!content.trim() || (!messageHasPlaceholders(content) && !hasBookingLinkToken)) return null;

  const merged: MessageVarContext = { ...SAMPLE_PLACEHOLDER_CONTEXT };
  if (recipientContext) {
    for (const [key, value] of Object.entries(recipientContext)) {
      if (value != null && String(value).trim() !== "") {
        (merged as Record<string, string>)[key] = String(value);
      }
    }
  }

  const rendered = previewBookingLinkTokens(substituteMessageVars(content, merged));
  const usingReal = !!recipientName;

  return (
    <div
      className={`mt-2 rounded-md border border-blue-200 bg-blue-50 p-3 ${className ?? ""}`}
      data-testid="message-placeholder-preview"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-blue-700">
        <Eye className="h-3.5 w-3.5 shrink-0" />
        <span>Preview</span>
        <span className="font-normal text-blue-500" data-testid="text-preview-source">
          {usingReal ? `Using ${recipientName}'s details` : "Using sample details"}
        </span>
      </div>
      <p
        className="whitespace-pre-wrap break-words text-sm text-gray-800"
        data-testid="text-message-placeholder-preview"
      >
        {rendered}
      </p>
    </div>
  );
}
